import React, { ReactElement, useState, useMemo, SetStateAction, useEffect, lazy } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { Translate } from "react-redux-i18n";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import { Masonry } from "@mui/lab";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import itLocale from "date-fns/locale/it";
import esLocale from "date-fns/locale/es";
import enLocale from "date-fns/locale/en-GB";
import ptLocale from "date-fns/locale/pt-BR";

import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { CircularProgress, Grid } from "@mui/material";

import PrintOddsPdf from "../PrintOddsPdf";
import { IPalimpsestItem, ITournament, getPrintOddRequested } from "redux/sport/sportSlice";
import { endOfWeek, format, startOfWeek } from "date-fns";

import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import { SPORT_PRINT_ODD_DATE_OPTIONS } from "../constants";
import styles from "./styles.module.scss";
import ReactToPrint from "react-to-print";

const PrintOddsDocument = lazy(() => import("../PrintOddsDocument"))

interface IProps {
  itemsToLoad: number;
  setItemsToLoad: React.Dispatch<SetStateAction<number>>
}
interface IPayload {
  start_date: string;
  end_date: string;
}


function PrintOdds({ itemsToLoad, setItemsToLoad }: IProps): ReactElement {
  const dispatch = useAppDispatch();
  const lang = useAppSelector((state) => state.auth.locale);
  const oddsToPrint = useAppSelector(state => state.sport.oddsToPrint);
  const isFetchingPrintOdd = useAppSelector(state => state.sport.isFetchingPrintOdd);
  const sports = useAppSelector(state => state.sport.sports);
  const marketsConfig = useAppSelector((state) => state.sport.marketsConfig);
  const groupsConfig = useAppSelector((state) => state.sport.groupsConfig)
  const [order, setOrder] = useState<string>("tournament")
  const [dateRange, setDateRange] = useState<IPayload>({
    start_date: format(
      new Date(),
      "yyyy-MM-dd 00:00:00"
    ),
    end_date: format(
      new Date(),
      "yyyy-MM-dd 23:59:59"
    ),
  })
  const [selectedSport, setSelectedSport] = useState<number | undefined>(undefined)
  const [selectedTournaments, setSelectedTounaments] = useState<number[]>([])
  const [isAllSelected, setAllSelected] = useState(false)
  const [selectedMarkets, setSelectedMarkets] = useState<number[]>([])
  const [isAllMarketsSelected, setAllMarketsSelected] = useState(false)
  const [showMessage, setShowMessage] = useState(false);
  const [isPdfVisible, setIsPdfVisible] = useState(false)
  const [orientation, setOrientation] = useState("landscape")

  const selectedSportCategory = useMemo(() => sports?.find(sport => sport.id === selectedSport)?.children || [], [selectedSport])
  const selectMarketsConfig = useMemo(() => marketsConfig?.filter((element) => element.sport_id === selectedSport), [selectedSport])

  const handleChange = (name: string, value: string) => {
    setDateRange({
      ...dateRange,
      [name]: value
    })
  }
  const handleSportChange = (value: number) => {
    setSelectedSport(value)
  }

  const handleDateChange = (value: string) => {
    const currentDate = new Date();
    const nextThreeMonths = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 1);

    const lastSundayOfNextThreeMonths = new Date(nextThreeMonths);
    lastSundayOfNextThreeMonths.setMonth(lastSundayOfNextThreeMonths.getMonth() + 1);
    lastSundayOfNextThreeMonths.setDate(0);

    while (lastSundayOfNextThreeMonths.getDay() !== 0) {
      lastSundayOfNextThreeMonths.setDate(lastSundayOfNextThreeMonths.getDate() - 1);
    }

    switch (value) {
      case "All":
        setDateRange({
          ...dateRange,
          start_date: format(
            new Date(),
            "yyyy-MM-dd 00:00:00"
          ),
          end_date: format(
            lastSundayOfNextThreeMonths,
            "yyyy-MM-dd 23:59:59"
          ),
        });
        break;
      case "thisWeek":
        setDateRange({
          ...dateRange,
          start_date: format(
            startOfWeek(
              new Date(
                Date.now()),
              { weekStartsOn: 1 }
            ),
            "yyyy-MM-dd 00:00:00"
          ),
          end_date: format(
            endOfWeek(
              new Date(
                Date.now()),
              { weekStartsOn: 1 }
            ),
            "yyyy-MM-dd 23:59:59"
          ),
        });

        break;
      case "nextWeek":
        setDateRange({
          ...dateRange,
          start_date: format(
            new Date().setDate(new Date().getDate() + 6),
            "yyyy-MM-dd 00:00:00"
          ),
          end_date: format(
            new Date().setDate(new Date().getDate() + 13),
            "yyyy-MM-dd 23:59:59"
          ),
        });
        break;
      case "today":
        setDateRange({
          ...dateRange,
          start_date: format(
            new Date(),
            "yyyy-MM-dd 00:00:00"
          ),
          end_date: format(
            new Date(),
            "yyyy-MM-dd 23:59:59"
          ),
        });
        break;
      case "today +1":
        setDateRange({
          ...dateRange,
          start_date: format(
            new Date().setDate(new Date().getDate() + 1),
            "yyyy-MM-dd 00:00:00"
          ),
          end_date: format(
            new Date().setDate(new Date().getDate() + 1),
            "yyyy-MM-dd 23:59:59"
          ),
        });
        break;
      case "today +2":
        setDateRange({
          ...dateRange,
          start_date: format(
            new Date().setDate(new Date().getDate() + 2),
            "yyyy-MM-dd 00:00:00"
          ),
          end_date: format(
            new Date().setDate(new Date().getDate() + 2),
            "yyyy-MM-dd 23:59:59"
          ),
        });
        break;
    }
  }

  const handleSelectAllMarkets = () => {
    const allMarketsIDs = selectMarketsConfig?.map(marketConfig => marketConfig.id) || [];
    setAllMarketsSelected(!isAllMarketsSelected)
    if (isAllMarketsSelected) {
      setSelectedMarkets([])
    }
    else {
      setSelectedMarkets(allMarketsIDs)
    }
  }

  const handleSelectMainMarkets = () => {
    const mainMarketGroupsIDs = groupsConfig?.filter(groupConfig => groupConfig.main).map(gr => gr.id);
    const mainMarkets = selectMarketsConfig?.filter(el => mainMarketGroupsIDs?.includes(el.group_id))
    const mainMarketsId = mainMarkets?.map(el => el?.id)

    if (mainMarketsId) {
      const isSelectedAllOnMarkets = mainMarketsId && mainMarketsId.every(a => a && selectedMarkets.includes(a))
      if (isSelectedAllOnMarkets === false) {
        setSelectedMarkets([...selectedMarkets, ...mainMarketsId]);
      } else {
        setSelectedMarkets(selectedMarkets.filter(id => !mainMarketsId.includes(id)))
      }
    }
  }

  const isCategoryAllSelected = (categoryIDs: number[]) => {
    return categoryIDs.every(a => selectedTournaments.includes(a))
  }

  const handleSelectCategory = (categoryGotFromClick: any) => {
    const currentCategoryTrounamentsIDs = (categoryGotFromClick.children || []).map((trn: ITournament) => trn.id);
    const isSelectedAllOnCategory = isCategoryAllSelected(currentCategoryTrounamentsIDs)
    if (isSelectedAllOnCategory === false) {
      setSelectedTounaments([...selectedTournaments, ...currentCategoryTrounamentsIDs])
    } else {
      setSelectedTounaments(selectedTournaments.filter(id => !currentCategoryTrounamentsIDs.includes(id)))
    }

  }
  const handleSelectSingleTournament = (id: number) => {
    const isChecked = selectedTournaments.includes(id);
    if (isChecked) {
      setSelectedTounaments(selectedTournaments.filter(tId => tId !== id))
    }
    else {
      setSelectedTounaments([...selectedTournaments, id])
    }
  }
  const handleSingleMarketCheck = (id: number) => {
    const isChecked = selectedMarkets.includes(id);
    if (isChecked) {
      setSelectedMarkets(selectedMarkets.filter(mId => mId !== id))
    }
    else {
      setSelectedMarkets([...selectedMarkets, id])
    }
  }

  const handleCreatePdf = () => {
    if (selectedTournaments.length === 0 || selectedMarkets.length === 0) {
      setShowMessage(true);
    } else {
      setShowMessage(false);
      const data = {
        tournaments: selectedTournaments,
        markets: selectedMarkets,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      };
      dispatch(getPrintOddRequested(data))
      oddsToPrint && setIsPdfVisible(true)
    }
  }

  useEffect(() => {
    setItemsToLoad(4)
  }, [])

  return (
    <>
      {!isPdfVisible ?
        <Box className={styles.container}>
          {/* filters */}
          <Box className={styles.filtersContainer}>
            <Box className={styles.filters}>
              {/* sports */}
              <TextField
                select
                value={selectedSport}
                label={<Translate value="sport.selectSports" />}
                size="small"
                color="secondary"
                onChange={(event) => handleSportChange(Number(event.target.value))}
                inputProps={{
                  MenuProps: {
                    className: styles.selectMenu,
                  },
                }}
                className={[styles.textfield, "form-label"].join(" ")}
              >
                {
                  (sports || []).map((option) => {
                    return (
                      <MenuItem
                        divider
                        key={option.id}
                        value={option.id}
                        className={styles.optionLabel}
                      >
                        <Translate value={`${option?.label}`} />
                      </MenuItem>
                    )
                  })
                }
              </TextField>
              {/* date options */}
              <TextField
                select
                name="selectedDate"
                label={<Translate value="sport.selectDate" />}
                size="small"
                color="secondary"
                onChange={(event) => handleDateChange(event.target.value)}
                inputProps={{
                  MenuProps: {
                    className: styles.selectMenu,
                  },
                }}
                className={[styles.textfield, "form-label"].join(" ")}
              >
                {
                  SPORT_PRINT_ODD_DATE_OPTIONS
                    .map((option) => {
                      return (
                        <MenuItem
                          divider
                          key={option.value}
                          value={option.value}
                          className={styles.optionLabel}
                        >
                          <Translate value={`sport.${option?.label}`} />
                        </MenuItem>
                      )
                    })
                }
              </TextField>
              {/* date ranges */}
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                locale={
                  lang === "it" ?
                    itLocale
                    :
                    lang === "es" ?
                      esLocale
                      :
                      lang === "pt" ?
                        ptLocale
                        :
                        enLocale
                }
              >
                <DatePicker
                  value={dateRange.start_date}
                  inputFormat={process.env.REACT_APP_DATE_FORMAT}
                  maxDate={new Date(dateRange.end_date)}
                  onChange={(value) => {
                    value &&
                      value !== null &&
                      handleChange("start_date", format(new Date(value), "yyyy-MM-dd 00:00:00"))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      variant="outlined"
                      label={<Translate value="transactionFilter.from" />}
                      className={[styles.textfield, "form-label"].join(" ")}
                    />
                  )}
                  className={styles.datepicker}
                />
              </LocalizationProvider>

              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                locale={
                  lang === "it" ?
                    itLocale
                    :
                    lang === "es" ?
                      esLocale
                      :
                      lang === "pt" ?
                        ptLocale
                        :
                        enLocale
                }
              >
                <DatePicker
                  value={dateRange.end_date}
                  minDate={new Date(dateRange.start_date)}
                  inputFormat={process.env.REACT_APP_DATE_FORMAT}
                  onChange={(value) => {
                    value &&
                      value !== null &&
                      handleChange("end_date", format(new Date(value), "yyyy-MM-dd 23:59:59"))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      variant="outlined"
                      className={[styles.textfield, "form-label"].join(" ")}
                      label={<Translate value="transactionFilter.to" />}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
            <RadioGroup value={order} onChange={(e) => {
              setOrder(e.target.value)
            }} row className={styles.orders}>
              <FormControlLabel
                value="tournament"
                control={<Radio size="small" color="success" className={styles.radioButton} />}
                label={<Translate value="sport.tournaments" />} />
              <FormControlLabel
                value="date"
                control={<Radio size="small" color="success" className={styles.radioButton} />}
                label={<Translate value="sport.data" />} />
              <FormControlLabel
                value="date-tournament"
                control={<Radio size="small" color="success" className={styles.radioButton} />}
                label={<Translate value="sport.tournaments/data" />} />
            </RadioGroup>
          </Box>
          {/* categories && markets */}
          {selectedSportCategory.length > 0 &&
            <Box className={styles.itemsContainer}>
              {/* toolbar*/}
              <Box className={styles.btnBar}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCreatePdf}
                  className={styles.pdfButton}
                >
                  <Typography className={styles.createpdfBtn}>
                    <Translate value="sport.createButton" />
                  </Typography>
                </Button>
              </Box>

              <Masonry columns={2} >
                {/* categories */}
                <Grid item xs={12} md={4} className={styles.gridItem} >
                  <Box className={styles.categories}>
                    {
                      selectedSportCategory
                        .map((category) => {
                          return (
                            <Accordion
                              key={category.id}
                              className={styles.accordionContainer} >
                              <AccordionSummary
                                className={styles.accordionSummary}
                                expandIcon={<Box className={styles.expandIcon}><FontAwesomeIcon name="chevron-down" type="fas" color="white" /></Box>}>
                                <Box className={styles.categoryBox}>
                                  <Checkbox
                                    onChange={() => handleSelectCategory(category)}
                                    checked={isAllSelected || (category.children && isCategoryAllSelected(category.children.map((trn: IPalimpsestItem) => trn.id)))}
                                    color="secondary"
                                    className={`${styles.checkbox}`}
                                  />
                                  <Typography className={styles.labelClass}>
                                    {category.label}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails className={styles.accordionDetails}>
                                {
                                  (category.children || [])
                                    .map(tournament => {
                                      return (
                                        <Box key={tournament.id} className={styles.tournamentRow}>
                                          <Checkbox
                                            id={`checkbox-${tournament.id}`}
                                            onChange={() => handleSelectSingleTournament(tournament.id)}
                                            checked={selectedTournaments.includes(tournament.id)}
                                            color="secondary"
                                            className={styles.checkbox}
                                          />
                                          <label htmlFor={`checkbox-${tournament.id}`} >
                                            <Typography className={styles.checkboxesLabel}>
                                              {tournament.label}
                                            </Typography>
                                          </label>
                                        </Box>
                                      )
                                    })}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                  </Box >
                </Grid >
                {/* markets */}
                <Grid item xs={12} md={8} className={styles.gridItem} >
                  <Box className={styles.marketsCheckboxes}>
                    <Box className={styles.checkGroups}>
                      <Checkbox
                        value={selectedMarkets}
                        onChange={handleSelectMainMarkets}
                        color="secondary"
                        className={styles.checkbox}
                      />
                      <Typography className={styles.checkboxesLabel}>
                        <Translate value="sport.mainMarkets" />
                      </Typography>
                    </Box>
                    <Box className={styles.checkGroups}>
                      <Checkbox
                        onChange={handleSelectAllMarkets}
                        color="secondary"
                        className={styles.checkbox}
                      />
                      <Typography className={styles.checkboxesLabel}>
                        <Translate value="sport.allMarkets" />
                      </Typography>
                    </Box>

                  </Box>

                  <Box className={styles.markets}>

                    {
                      selectMarketsConfig &&
                      selectMarketsConfig.map((value) => {
                        return (
                          <Box key={value.id} className={styles.marketItem}>
                            <Checkbox
                              checked={selectedMarkets.includes(value.id)}
                              onChange={() => handleSingleMarketCheck(value.id)}
                              color="secondary"
                              className={styles.checkbox}
                            />
                            <Typography key={value.id} className={styles.market}>
                              {value.label}
                            </Typography>
                          </Box>
                        )
                      })}
                  </Box>
                </Grid >
              </Masonry >
            </Box >
          }

        </Box >
        :
        !isFetchingPrintOdd ?
          oddsToPrint.length > 0 ?
            <Box className={styles.printContainer}>
              <Box className={styles.buttonsContainer}>
                <ReactToPrint
                  trigger={() =>
                    <Button color="success" variant="contained">Print</Button>
                  }
                  content={() => document.getElementsByClassName("printDocPortal")[0]}
                  pageStyle={`@page {
                  size: A4 ${orientation};
                margin: 5 !important;
              }`}
                />
                <RadioGroup row>
                  <FormControlLabel
                    value="landscape"
                    control={
                      <Radio
                        checked={orientation === "landscape" ? true : false}
                        onChange={(e) => setOrientation(e.currentTarget.value)}
                        color="success"
                        className={styles.radioLabel}
                      />
                    }
                    label={<Typography className={styles.radioLabel}><Translate value="sport.landscape" /></Typography>}
                  />
                  <FormControlLabel
                    value="portrait"
                    control={
                      <Radio
                        checked={orientation === "portrait" ? true : false}
                        onChange={(e) => setOrientation(e.currentTarget.value)}
                        color="success"
                        className={styles.radioLabel}
                      />
                    }
                    label={<Typography className={styles.radioLabel}><Translate value="sport.portrait" /></Typography>}
                  />
                </RadioGroup>
              </Box>
              <Box
                className={styles.pdfContainer}
              >
                <PrintOddsPdf
                  selectedMarkets={selectedMarkets}
                  order={order}
                  orientation={orientation}
                  itemsToLoad={itemsToLoad}
                />
                <PrintOddsDocument
                  order={order}
                  orientation={orientation}
                  selectedMarkets={selectedMarkets}
                />
              </Box>
            </Box>
            :
            <Box className={styles.loadingPdf}>
              <Typography><Translate value="sport.noPrintData" /></Typography>
            </Box>
          :
          <Box className={styles.loadingPdf}>
            <CircularProgress />
            <Typography><Translate value="sport.loadPdf" /></Typography>
          </Box>
      }
    </>
  );
}
export default PrintOdds;

